/* eslint-disable */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './FullWidthECGPreview.module.css'
import Beat from './Beats';
import Grid from './Grid'
import Waveform from './Waveform'
import toast from 'react-hot-toast'
import * as d3 from 'd3';
import * as Lib from "../../constants/annotator"
import { DEFAULT_BEAT_LABELS } from "../../utils/universalFormatter"

const FullWidthECGPreview = (props) => {
    const {
        ecgTitle = null,
        seconds = 30,
        width = seconds*Lib.PX_PER_SECOND,
        height = Lib.MV_BANDWIDTH * Lib.PX_PER_MV,
        scale = 1,
        heightFactor = 1,
        mainsFreq = 60,
        sample = [],
        style = null,
        enableBeats = false,
        beats =[],
        mvSwing = 1,
        onClick,
        onRightClick,
        onFiducialPointDrag,
        renderTimeScaleFactor = null,
        frequency,
        offsetGrid = false,
        beatLabel = DEFAULT_BEAT_LABELS,
        fiducialPoints = [],
        ghostFiducialPoint = null,
        fiducialPointLabel = [],
        hideGrid = false,
        waveformStrokeColor = null,
    } = props

    const [draggingType, setDraggingType] = useState(null)

    useEffect(() => {
        if (frequency == null) {
            toast.error("FullWidthECGPreview: sampling rate missing — cannot render ECG correctly.", { id: 'fullwidthecgpreview-frequency-missing' })
        }
    }, [frequency])

    if (frequency == null) return null

    const geomLen = sample.length

    // Match the exact scaling used by Beat.jsx for perfect alignment
    const plotHeight = height * heightFactor * scale
    const stripTimeSec = geomLen / frequency
    const stripWidthPx = stripTimeSec * Lib.PX_PER_SECOND * scale
    const xScaleFn = d3.scaleLinear().domain([0, geomLen]).range([0, stripWidthPx])
    const yScaleFn = d3.scaleLinear().domain([(mvSwing * -1), mvSwing]).range([plotHeight, 0])

    const renderFiducialPoints = () => {
        // No waveform means no meaningful geometry to anchor points to
        // (empty sample => geomLen=0 collapses every point to x=0).
        if (geomLen === 0) return null
        return fiducialPoints.map((fp) => {
            const idx = Math.max(0, Math.min(Math.max(geomLen - 1, 0), fp.sample))
            const x = xScaleFn(idx)
            const y = yScaleFn(sample[idx] || 0)
            const label = fiducialPointLabel.find(l => l.slug === fp.type)
            const handleMouseDown = (e) => {
                if (!onFiducialPointDrag) return
                e.stopPropagation()
                e.preventDefault()
                setDraggingType(fp.type)
            }
            // Wide invisible hit strip along the fiducial's vertical line so
            // the point is draggable from anywhere along its x-column, not just
            // the small circle. This matters in single-column median-beat view
            // where per-lead tiles overlap and the topmost tile's circle covers
            // markers on other leads at the same fiducial x.
            const hitWidth = 12
            return (
                <g key={fp.type}>
                    <rect
                        x={x - hitWidth / 2}
                        y={0}
                        width={hitWidth}
                        height={plotHeight}
                        fill="transparent"
                        style={{ cursor: onFiducialPointDrag ? "grab" : "default" }}
                        onMouseDown={handleMouseDown}
                    />
                    <line x1={x} y1="0" x2={x} y2={plotHeight} stroke={label?.color || "#000"} strokeWidth="2" strokeDasharray="4" opacity="0.5" pointerEvents="none" />
                    <circle
                        cx={x}
                        cy={y}
                        r="6"
                        fill={label?.color || "#000"}
                        style={{ cursor: onFiducialPointDrag ? "grab" : "default" }}
                        onMouseDown={handleMouseDown}
                    />
                </g>
            )
        })
    }

    const renderGhostFiducialPoint = () => {
        if (!ghostFiducialPoint || ghostFiducialPoint.sampleIndex == null) return null
        if (geomLen === 0) return null
        const x = xScaleFn(ghostFiducialPoint.sampleIndex)
        const y = yScaleFn(sample[ghostFiducialPoint.sampleIndex] || 0)
        return (
            <g>
                <line x1={x} y1="0" x2={x} y2={plotHeight} stroke="#888" strokeWidth="2" strokeDasharray="4" opacity="0.5" />
                <circle cx={x} cy={y} r="6" fill="#888" opacity="0.5" />
            </g>
        )
    }

    // The container div stretches to fill its parent, but the SVG is only
    // stripWidthPx wide. Measure the SVG so click/drag positions map to
    // sample indices even when the container is wider than the waveform
    // (e.g., single-column median-beat view).
    const svgRef = useRef(null)

    const handleContextMenu = (e) => {
        if (!onRightClick) return
        e.preventDefault()
        if (!stripWidthPx || geomLen === 0) return
        const rect = svgRef.current?.getBoundingClientRect()
        if (!rect) return
        const clickX = e.clientX - rect.left
        // Ignore right-clicks that land outside the waveform (the container div
        // can be wider than the SVG in single-column layouts).
        if (clickX < 0 || clickX > stripWidthPx) return
        const sampleIndex = Math.min(geomLen - 1, Math.round((clickX / stripWidthPx) * (geomLen - 1)))
        onRightClick(e, sampleIndex)
    }

    useEffect(() => {
        if (!draggingType || !onFiducialPointDrag) return
        let lastSampleIndex = null
        const handleMove = (e) => {
            if (!stripWidthPx || geomLen === 0) return
            const rect = svgRef.current?.getBoundingClientRect()
            if (!rect) return
            const clickX = e.clientX - rect.left
            const clamped = Math.max(0, Math.min(stripWidthPx, clickX))
            const sampleIndex = Math.min(geomLen - 1, Math.round((clamped / stripWidthPx) * (geomLen - 1)))
            if (sampleIndex === lastSampleIndex) return
            lastSampleIndex = sampleIndex
            onFiducialPointDrag(draggingType, sampleIndex)
        }
        const handleUp = () => setDraggingType(null)
        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleUp)
        return () => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleUp)
        }
    }, [draggingType, onFiducialPointDrag, stripWidthPx, geomLen])

    return (
        <div
            style={{height:`${height*heightFactor * scale}`,...style}}
            onClick={onClick ? ()=>onClick() : undefined}
            onContextMenu={handleContextMenu}
            className={styles.FullWidthECGPreviewContainer}
        >
            <svg ref={svgRef} width={width * scale} height={`${height*heightFactor * scale}px`} className={styles.previewGrid}>
                {ecgTitle && ecgTitle.includes("-syn") && <rect x="4" y="4" width={`${60*scale}`} height={`${20*scale}`} style={{"stroke":"black","strokeWidth":2,"fillOpacity":0,"strokeOpacity":0.5}} />}
                {ecgTitle && <text x="9" y={`${19*scale}`} className={styles.LeadTitle} style={{fontSize: `${15*scale}px`}}>{ecgTitle}</text>}
                {enableBeats && beats != [] && beats.map(beat =>
                    <Beat
                        beatLabel={beatLabel}
                        key ={beat.i}
                        capsule={false}
                        height={height*heightFactor * scale}
                        samples={sample}
                        index={beat.i}
                        type={beat.t}
                        mvSwing={mvSwing}
                        frequency={frequency}
                        scale={scale}
                    />
                )}
                {renderGhostFiducialPoint()}
                {renderFiducialPoints()}
                <g transform="translate(0,0)">
                <Waveform
                    capsule={false}
                    height={height*heightFactor * scale}
                    samples={sample}
                    mainsFreq={mainsFreq}
                    mvSwing={mvSwing}
                    frequency={frequency}
                    waveformStrokeColor={waveformStrokeColor || (ecgTitle && ecgTitle.includes("-syn") ? "#000" : "#bb2739")}
                    scale={scale}
                />
                {!hideGrid && (
                    <Grid
                        renderSmallGrid={true}
                        rowSeconds={seconds}
                        stripWidthPx={width * scale}
                        height={height*heightFactor * scale}
                        renderTimeScaleFactor={renderTimeScaleFactor}
                        offsetGrid={offsetGrid}
                        scale={scale}
                    />
                )}
                </g>
            </svg>
        </div>
    )
}

export default FullWidthECGPreview
